import { Box, Typography, Button } from "@mui/material";
import TermsOfServicePolicy from "Components/TermsOfServicePolicy";
import React, { Suspense, lazy, useMemo } from "react";
import {
  Route,
  Routes,
  Navigate,
  useSearchParams,
  useParams,
  useNavigate,
} from "react-router-dom";
import { useQuery } from "@apollo/client";
import Forbidden from "./errors/forbidden";
import { useKnowledgeBaseStore } from "./KnowledgeBaseIndex/stores/knowledgeBaseStore";
import { fromGraphQLToActiveFilters } from "./KnowledgeBaseIndex/utils/filterHelpers";
import { SAVED_KNOWLEDGE_BASE_QUERY } from "./KnowledgeBaseIndex/graphql";
import Main from "./main";
import SettingsLoader from "./SettingsLoader";
import NotFound from "./show/not_found.js.jsx";
import { VersionChecker } from "./ApplicationVersionCheck";
import { Authorized } from "./Authorized";
import { useCurrentUser } from "frontend/src/utils/hooks";
import { UserFlagCheck } from "./Authorized/Authorized";
import { ORG_MANAGEMENT_CHECKS } from "./Authorized/authHelpers";
import { usePortfolioSearch } from "./GlobalSearchWorkspace/InvestmentSearch.hooks";
import { ClientViewCheck } from "./ClientView";
import { ErrorBoundary } from "./ErrorBoundary";
import { Spinner } from "Components/show/spinner.js";

// Lazy-loaded route components
const GlobalNotesComponent = lazy(() =>
  import("./GlobalNotes").then(m => ({ default: m.GlobalNotesComponent }))
);
const GlobalDocuments = lazy(() =>
  import("./global_documents").then(m => ({ default: m.GlobalDocuments }))
);
const Settings = lazy(() => import("./Settings"));
const Admin = lazy(() => import("./Admin"));
const Notices = lazy(() => import("./Notices"));
const Organization = lazy(() => import("./Organization"));
const KnowledgeBaseIndex = lazy(
  () => import("./KnowledgeBaseIndex/KnowledgeBaseIndex.component")
);
const PdfShowPage = lazy(() => import("./show/documents/documentViewer"));
const PdfNativeViewer = lazy(
  () => import("./show/documents/documentViewer/pdfNativeViewer")
);
const MultiDocumentUpload = lazy(
  () => import("frontend/src/components/MultiDocumentUpload")
);
const FirmPage = lazy(() => import("./FirmPage"));
const InvestmentSearchWorkspace = lazy(() =>
  import("./GlobalSearchWorkspace/InvestmentSearchWorkspace.component").then(
    m => ({ default: m.InvestmentSearchWorkspace })
  )
);
const InvestmentPage = lazy(() => import("./InvestmentPage"));
const GlobalFieldsComponent = lazy(() =>
  import("./GlobalFields").then(m => ({ default: m.GlobalFieldsComponent }))
);
const PortfolioWorkspace = lazy(() =>
  import("./GlobalSearchWorkspace/PortfolioWorkspace.component").then(m => ({
    default: m.PortfolioWorkspace,
  }))
);
const CompanySearchWorkspace = lazy(() =>
  import("./GlobalSearchWorkspace/CompanySearchWorkspace.component").then(
    m => ({ default: m.CompanySearchWorkspace })
  )
);
const EngagementSearchWorkspace = lazy(() =>
  import("./GlobalSearchWorkspace/EngagementSearchWorkspace.component").then(
    m => ({ default: m.EngagementSearchWorkspace })
  )
);
const CompanyPage = lazy(() => import("./CompanyPage"));
const EngagementPage = lazy(() => import("./EngagementPage"));
const InternalDashboards = lazy(() =>
  import("./InternalDashboards").then(m => ({ default: m.InternalDashboards }))
);
const AssetAllocationPage = lazy(() =>
  import("./AssetAllocation/Page/AssetAllocationPage").then(m => ({
    default: m.AssetAllocationPage,
  }))
);

const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary
    fallback={({ error }) => (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
        py={4}
      >
        <Typography variant="h6" color="error">
          Failed to load this page
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message.includes("chunk") || error.message.includes("Loading")
            ? "A newer version may be available."
            : "An unexpected error occurred."}
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Box>
    )}
  >
    <Suspense fallback={<Spinner />}>{children}</Suspense>
  </ErrorBoundary>
);

const KnowledgeBaseRoute = () => {
  const [searchParams] = useSearchParams();
  const kbParam = searchParams.get("kb");
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const numericId = id ? Number(id) : null;

  const setActiveFilters = useKnowledgeBaseStore(s => s.setActiveFilters);
  const setSearchText = useKnowledgeBaseStore(s => s.setSearchText);
  const setSortField = useKnowledgeBaseStore(s => s.setSortField);
  const setSortDirection = useKnowledgeBaseStore(s => s.setSortDirection);
  const setLoadedKnowledgeBase = useKnowledgeBaseStore(
    s => s.setLoadedKnowledgeBase
  );

  // /knowledgeBase/:id — load the saved KB by id and hydrate the index.
  const { data, error } = useQuery(SAVED_KNOWLEDGE_BASE_QUERY, {
    variables: numericId ? { id: numericId } : undefined,
    skip: !numericId,
    fetchPolicy: "cache-and-network",
  });

  React.useEffect(() => {
    if (!numericId) {
      // /knowledgeBase (no id) — clear loaded KB context.
      setLoadedKnowledgeBase(null);
      return;
    }
    if (error) {
      // Not found / forbidden — redirect to the index.
      setLoadedKnowledgeBase(null);
      navigate("/knowledgeBase", { replace: true });
      return;
    }
    const kb = data?.savedKnowledgeBase;
    if (!kb) return;
    const filters = kb.filters || {};
    const activeFilters = fromGraphQLToActiveFilters(filters);
    setActiveFilters(activeFilters);
    setSearchText(typeof filters.text === "string" ? filters.text : "");
    setLoadedKnowledgeBase({
      id: kb.id,
      name: kb.name,
      groupId: kb.groupId ?? null,
      filters,
    });
  }, [
    numericId,
    data,
    error,
    navigate,
    setActiveFilters,
    setSearchText,
    setLoadedKnowledgeBase,
  ]);

  // ?kb=<base64> path — only honored when there's no :id.
  React.useEffect(() => {
    if (numericId) return;
    if (!kbParam) {
      setActiveFilters({});
      setSearchText("");
      return;
    }
    try {
      const kbState = JSON.parse(atob(kbParam));
      if (kbState.filters) {
        const activeFilters = fromGraphQLToActiveFilters(kbState.filters);
        setActiveFilters(activeFilters);
      }
      if (kbState.searchText) {
        setSearchText(kbState.searchText);
      }
      if (kbState.sort?.field) {
        setSortField(kbState.sort.field);
      }
      if (kbState.sort?.order) {
        setSortDirection(kbState.sort.order);
      }
    } catch {
      // Invalid kb param — ignore
    }
  }, [
    numericId,
    kbParam,
    setActiveFilters,
    setSearchText,
    setSortField,
    setSortDirection,
  ]);

  return <KnowledgeBaseIndex />;
};

// static props to avoid re-rendering
const isRockCreekCheck: UserFlagCheck[] = ["isRockCreek"];
const isSubscriptionUserCheck: UserFlagCheck[] = ["isSubscriptionUser"];
// Entry gate for /admin/* — system administration, super users only.
const adminChecks: UserFlagCheck[] = ["isSuperUser"];
// Entry gate for /organization/* — org-scoped self-service settings (#4889:
// templates + custom fields). Shared with the navbar menu entry so the two can't
// drift (ORG_MANAGEMENT_CHECKS = super users OR org admins, DCAdmin; the latter
// are locked to their own org by the resolvers).
const organizationChecks = ORG_MANAGEMENT_CHECKS;
const companyListChecks: UserFlagCheck[] = isSubscriptionUserCheck;
const companyShowChecks: UserFlagCheck[] = isSubscriptionUserCheck;
const investmentPageChecks: UserFlagCheck[] = ["investmentPageAccess"];
// Notice review and per-notice data; the API limits each notice to users who can read its document.
const noticesChecks: UserFlagCheck[] = ["coreUser"];
// Diligence (Field) search is a subscriber feature, not a Rock Creek-only one:
// the Diligence tab in GlobalSearchTotals is already shown to isSubscriptionUser,
// and the backing fieldList/fieldListDownload/fieldKeysByQuestion queries are
// gated on CORE_USER with row-level access enforced by the Field loaders.
const diligenceSearchChecks: UserFlagCheck[] = isSubscriptionUserCheck;

export const HomeRoute = () => {
  const user = useCurrentUser();

  const { result } = usePortfolioSearch();
  const homePage = useMemo(() => {
    if (user.clientView) {
      if (result?.total) {
        return "/portfolios";
      }
    }
    if (user.flags?.investmentPageAccess) {
      return "/investments";
    } else if (user.flags?.isSubscriptionUser) {
      return "/company";
    } else {
      return "/notes";
    }
  }, [user, result]);

  if (!result) {
    return null;
  }

  return <Navigate to={homePage} replace={true} />;
};

const MercuryApp = () => (
  <SettingsLoader>
    <TermsOfServicePolicy>
      <VersionChecker />
      <ClientViewCheck />

      <Routes>
        <Route path="/" element={<HomeRoute />} />

        <Route
          path="/notes"
          element={
            <Main type="tile">
              <LazyRoute>
                <GlobalNotesComponent />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/notes/*"
          element={
            <Main type="tile">
              <LazyRoute>
                <GlobalNotesComponent />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/documents"
          element={
            <Main type="tile">
              <LazyRoute>
                <GlobalDocuments />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/notices/*"
          element={
            <Authorized checks={noticesChecks} redirect>
              <Main type="tile">
                <LazyRoute>
                  <Notices />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/settings/*"
          element={
            <Main type="tile">
              <LazyRoute>
                <Settings />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/admin/*"
          element={
            <Authorized checks={adminChecks} redirect>
              <Main type="tile">
                <LazyRoute>
                  <Admin />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/organization/*"
          element={
            <Authorized checks={organizationChecks} redirect>
              <Main type="tile">
                <LazyRoute>
                  <Organization />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/knowledgeBase"
          element={
            <Authorized checks={isSubscriptionUserCheck} redirect>
              <Main type="tile">
                <ErrorBoundary>
                  <LazyRoute>
                    <KnowledgeBaseRoute />
                  </LazyRoute>
                </ErrorBoundary>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/knowledgeBase/:id"
          element={
            <Authorized checks={isSubscriptionUserCheck} redirect>
              <Main type="tile">
                <ErrorBoundary>
                  <LazyRoute>
                    <KnowledgeBaseRoute />
                  </LazyRoute>
                </ErrorBoundary>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/documents/:documentId"
          element={
            <Main type="tile">
              <LazyRoute>
                <PdfShowPage />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/documents/:documentId.pdf"
          element={
            <LazyRoute>
              <PdfNativeViewer />
            </LazyRoute>
          }
        />

        <Route
          path="/multiDocumentUpload"
          element={
            <Main type="centered">
              <LazyRoute>
                <MultiDocumentUpload />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/403"
          element={
            <Main type="centered">
              <Forbidden />
            </Main>
          }
        />

        <Route
          path="firms/:firmId/*"
          element={
            <Main type="centered">
              <LazyRoute>
                <FirmPage />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/engagements/:engagementId/*"
          element={
            <Main type="centered">
              <LazyRoute>
                <EngagementPage />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/investments"
          element={
            <Authorized checks={investmentPageChecks} redirect>
              <Main type="tile">
                <LazyRoute>
                  <InvestmentSearchWorkspace />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/investments/:investmentId/*"
          element={
            <Main type="centered">
              <LazyRoute>
                <InvestmentPage />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/diligenceSearch"
          element={
            <Authorized checks={diligenceSearchChecks} redirect>
              <Main type="tile">
                <LazyRoute>
                  <GlobalFieldsComponent />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/portfolios"
          element={
            <Main type="tile">
              <LazyRoute>
                <PortfolioWorkspace />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/company"
          element={
            <Authorized checks={companyListChecks} redirect>
              <Main type="tile">
                <LazyRoute>
                  <CompanySearchWorkspace />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/company/:companyId/*"
          element={
            <Authorized checks={companyShowChecks} redirect>
              <Main type="centered">
                <LazyRoute>
                  <CompanyPage />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/engagementsSearch"
          element={
            <Authorized checks={isSubscriptionUserCheck} redirect>
              <Main type="tile">
                <LazyRoute>
                  <EngagementSearchWorkspace />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        />

        <Route
          path="/internalDashboards"
          element={
            <Authorized checks={isRockCreekCheck} redirect>
              <Main type="centered">
                <LazyRoute>
                  <InternalDashboards />
                </LazyRoute>
              </Main>
            </Authorized>
          }
        >
          <Route
            path=":tab"
            element={
              <Authorized checks={isRockCreekCheck} redirect>
                <Main type="centered">
                  <LazyRoute>
                    <InternalDashboards />
                  </LazyRoute>
                </Main>
              </Authorized>
            }
          />
        </Route>

        <Route
          path="/assetAllocation"
          element={
            <Main type="centered">
              <LazyRoute>
                <AssetAllocationPage />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="/assetAllocation/:portfolioId"
          element={
            <Main type="centered">
              <LazyRoute>
                <AssetAllocationPage />
              </LazyRoute>
            </Main>
          }
        />

        <Route
          path="*"
          element={
            <Main type="tile">
              <NotFound />
            </Main>
          }
        />
      </Routes>
    </TermsOfServicePolicy>
  </SettingsLoader>
);

export default MercuryApp;
